<template>
  <q-field
    borderless
    class="relative-position"
    color="black"
    style="max-width: 350px;"
    :error="!!errorMessage"
    :error-message="errorMessage"
  >
    <img
      v-if="!!modelValue"
      style="width: 100%; height: 150px; max-width: 350px;"
      draggable="false"
      class="placeholder-sig"
      :src="modelValue"
      :class="{'cursor-pointer': !disable}"
      @click="enableEditing"
    />
    <div
      v-else
      class="placeholder-sig set-sig-width flex flex-center q-pa-md text-h6"
      :class="{'cursor-pointer': !disable}"
      @click="enableEditing"
    >
      <span class="opaque-60">No Signature</span>
    </div>

    <q-btn
      v-if="!disable"
      round
      color="blue"
      dense
      icon="mdi-pencil-outline"
      class="absolute-bottom-right"
      @click="enableEditing"
    />

    <content-dialog
      v-model="editing"
      transition-show="scale"
      width="380px"
      close-icon="mdi-arrow-left"
      without-blur
      :title="label"
      :no-backdrop-dismiss="$q.platform.is.mobile"
      @before-hide="saveSignature"
      @show="onDialogOpen"
    >
      <q-tabs
        v-model="mode"
        dense
        no-caps
        active-color="blue"
        indicator-color="blue"
        align="justify"
        class="text-grey-7"
      >
        <q-tab
          name="type"
          icon="mdi-keyboard-outline"
          label="Type"
        />
        <q-tab
          name="draw"
          icon="mdi-draw"
          label="Draw"
        />
      </q-tabs>
      <q-separator />

      <q-tab-panels v-model="mode" animated>
        <!-- Type a signature and pick a font (DocuSign-style) -->
        <q-tab-panel name="type" class="q-pa-sm">
          <q-input
            v-model="typedName"
            outlined
            dense
            maxlength="60"
            placeholder="Type your full name"
            class="q-mb-sm"
            @keyup.enter="$event.target.blur()"
          />
          <div class="column q-gutter-y-sm">
            <div
              v-for="font in fonts"
              :key="font"
              class="signature-font-option"
              :class="{
                'signature-font-option--selected': hasText && selectedFont === font,
                'signature-font-option--disabled': !hasText,
                'cursor-pointer': hasText
              }"
              @click="hasText && (selectedFont = font)"
            >
              <span :style="{ fontFamily: `'${font}'` }">{{ typedName || 'Your name' }}</span>
            </div>
          </div>
        </q-tab-panel>

        <!-- Draw a signature -->
        <q-tab-panel name="draw" class="q-pa-sm">
          <div class="flex justify-center">
            <VueSignaturePad
              ref="signature"
              :options="options"
              height="150px"
              class="shadow-2 set-sig-width signature-draw"
            />
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <template #bottom>
        <q-toolbar>
          <template v-if="mode === 'draw'">
            <q-btn
              no-caps
              flat
              label="Undo"
              color="blue-grey"
              icon="mdi-undo"
              @click="undoSignature"
            />
            <q-btn
              no-caps
              flat
              label="Clear"
              color="red"
              icon="mdi-format-clear"
              @click="clearSignature"
            />
          </template>
          <q-btn
            v-else
            no-caps
            flat
            label="Clear"
            color="red"
            icon="mdi-format-clear"
            :disable="!canClearType"
            @click="clearTyped"
          />

          <q-space />

          <q-btn
            v-close-popup
            label="Save"
            color="green"
            no-caps
            icon-right="mdi-check"
            :disable="mode === 'type' && !typedName.trim() && !signatureCleared"
          />
        </q-toolbar>
      </template>
    </content-dialog>
  </q-field>
</template>

<script>
/**
 * Reusable signature pad.
 *
 * Supports two ways to provide a signature:
 *  - Type: enter a name and pick a signature font; rendered to a canvas image
 *  - Draw: free-hand draw on a canvas
 *
 * Either way the v-model is a PNG data URL, updated when the dialog is closed
 * or "Save" is clicked.
 */
const SIGNATURE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script&family=Great+Vibes&family=Sacramento&family=Satisfy&display=swap'

/**
 * Pull the script fonts in on demand rather than from index.template.html.
 *
 * They were a render-blocking stylesheet in the app shell, so every page paid
 * for five fonts that only this component uses -- and in the Capacitor app that
 * is a request to Google's CDN on every launch, which stalls on poor signal and
 * fails outright offline. Here it costs nothing until someone opens a signature
 * pad, and renderTypedSignature() already awaits document.fonts.load() before
 * drawing, falling back to a system font if the fetch never lands.
 */
function ensureSignatureFonts () {
  if (typeof document === 'undefined' || document.getElementById('signature-fonts')) {
    return
  }

  const link = document.createElement('link')
  link.id = 'signature-fonts'
  link.rel = 'stylesheet'
  link.href = SIGNATURE_FONTS_HREF
  document.head.appendChild(link)
}

export default {
  props: {
    modelValue: {
      type: String,
      default: null
    },
    disable: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: 'Transcribe Signature'
    },
    errorMessage: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue', 'clear'],
  data () {
    return {
      editing: false,
      mode: 'type',
      typedName: '',
      signatureCleared: false,
      fonts: ['Dancing Script', 'Great Vibes', 'Sacramento', 'Allura', 'Satisfy'],
      selectedFont: 'Dancing Script',
      options: {
        penColor: 'rgb(0, 0, 0)',
        backgroundColor: 'rgb(255,255,255)'
      }
    }
  },
  computed: {
    hasText () {
      return !!this.typedName.trim()
    },
    // Something to clear from the type tab: a typed name, or an existing signature
    // that hasn't been cleared yet
    canClearType () {
      return !!this.typedName || (!!this.modelValue && !this.signatureCleared)
    }
  },
  watch: {
    'disable' (val) {
      if (val) {
        this.editing = false
      }
    },
    'mode' (val) {
      // Pre-fill the draw pad with the existing signature when switching to it
      // (unless it has been cleared)
      if (val === 'draw') {
        this.$nextTick(() => {
          if (this.$refs.signature && this.modelValue && !this.signatureCleared) {
            this.$refs.signature.fromDataURL(this.modelValue)
          }
        })
      }
    }
  },
  methods: {
    enableEditing () {
      if (this.disable) {
        return
      }
      this.editing = true
    },
    onDialogOpen () {
      this.typedName = ''
      this.selectedFont = this.fonts[0]
      this.signatureCleared = false
      // If a signature is already set, open on the draw tab (it shows the current
      // signature image, whether it was drawn or generated from the type tab)
      this.mode = this.modelValue ? 'draw' : 'type'
      this.$nextTick(() => {
        if (this.mode === 'draw' && this.$refs.signature && this.modelValue) {
          this.$refs.signature.fromDataURL(this.modelValue)
        }
      })
    },
    clearTyped () {
      this.typedName = ''
      this.signatureCleared = true
    },
    async saveSignature () {
      this.editing = false

      let data
      if (this.mode === 'type') {
        if (this.typedName.trim()) {
          data = await this.generateTypedSignature()
        }
        else if (this.signatureCleared) {
          data = null
        }
        else {
          // Empty name and nothing cleared — leave the existing signature untouched
          return
        }
      }
      else {
        data = this.drawnSignature()
      }

      this.$emit('update:modelValue', data)
      if (!data) {
        this.$emit('clear')
      }
    },
    drawnSignature () {
      if (!this.$refs.signature) {
        return this.modelValue
      }
      const { isEmpty, data } = this.$refs.signature.saveSignature()
      return isEmpty ? null : data
    },
    // Render the typed name in the selected font onto a canvas, returning a PNG data URL
    async generateTypedSignature () {
      const name = this.typedName.trim()
      if (!name) {
        return null
      }

      const width = 350
      const height = 150
      const font = this.selectedFont

      // Render at a higher resolution (like the drawn pad does via device pixel
      // ratio) so the signature stays crisp when scaled up, e.g. in the PDF.
      const scale = Math.max(window.devicePixelRatio || 1, 3)

      // Make sure the chosen font is loaded before drawing, else canvas falls back
      try {
        await document.fonts.load(`64px '${font}'`, name)
      }
      catch (err) {
        // Ignore — fall back to whatever is available
      }

      const canvas = document.createElement('canvas')
      canvas.width = width * scale
      canvas.height = height * scale
      const ctx = canvas.getContext('2d')
      // Draw using logical coordinates; the scale maps them onto the hi-res canvas
      ctx.scale(scale, scale)

      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, width, height)
      ctx.fillStyle = '#000000'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // Shrink the font until the name fits within the canvas width
      let fontSize = 64
      ctx.font = `${fontSize}px '${font}'`
      while (fontSize > 16 && ctx.measureText(name).width > width - 24) {
        fontSize -= 2
        ctx.font = `${fontSize}px '${font}'`
      }

      ctx.fillText(name, width / 2, height / 2)
      return canvas.toDataURL('image/png')
    },
    clearSignature () {
      if (this.$refs.signature) {
        this.$refs.signature.clearSignature()
      }
    },
    undoSignature () {
      if (this.$refs.signature) {
        this.$refs.signature.undoSignature()
      }
    }
  },
  mounted () {
    ensureSignatureFonts()
  }
}
</script>

<style scoped lang="scss">
.set-sig-width {
  width: 100%;
  max-width: 350px;
  height: 150px;
  border: 1px solid #ccc;
}

// Drawing is a press-and-drag, which a touch device otherwise reads as a
// scroll or a pull-to-dismiss: the page moves instead of a line appearing.
// Claim the gesture for the canvas -- only the pad in the dialog, so the inline
// box (tap to open) still lets the page scroll under a thumb.
.signature-draw,
.signature-draw :deep(canvas) {
  touch-action: none;
}

.placeholder-sig {
  box-shadow: $shadow-1;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.signature-font-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 72px;
  padding: 12px 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  overflow: hidden;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  span {
    // line-height leaves room for tall script ascenders/swashes so they aren't
    // clipped; nowrap keeps the signature on one line
    font-size: 28px;
    line-height: 1.6;
    white-space: nowrap;
    color: #000;
  }

  &--selected {
    border-color: $blue;
    background-color: $blue-1;
  }

  &--disabled {
    opacity: 0.5;
    cursor: default;
  }
}
</style>
